import type { FragmentType } from '@packages/gql/generated/shopping';
import { useAbTesting } from '@packages/abtesting';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { getQueryStringsFromUrl } from '@packages/shared';
import type {
  GtmProduct,
  PageMetaDataProps,
  ProductCustomDimensions,
  GtmEventPageNavigation,
  GtmEventProductView,
  GTMEventGlycerinDisplayProduct,
  GlycerinDisplayProductPayload,
  GlycerinService,
} from '@packages/tracking';
import {
  GlycerinRatingStatus,
  GlycerinAvailability,
  PageMetaData,
  useTracking,
} from '@packages/tracking';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useHashParameters, useAddLastSeen } from '../../hooks';

const TrackingFragment = graphql(/* GraphQL */ `
  fragment TrackingFragment on Product {
    akl
    availability {
      message
      state
    }
    brand {
      name
    }
    dimensions {
      type
      values {
        displayText
        isSelected
        availability {
          state
        }
      }
    }
    marketcode
    name
    orderNumber
    price {
      value
      valueOld
      currency
    }
    promotion
    sku
    seoRatingIcons: seoRating(locale: $locale) {
      product {
        avgRating
        reviewCount
      }
    }
    aplus(language: $lang) @skip(if: $skipAplus) {
      clusters {
        label
        services {
          mandatory
          gratis
          name
          preselected
          price
          serviceIcon
          uuid
        }
      }
    }
  }
`);

const TrackingBreadcrumbsFragment = graphql(/* GraphQL */ `
  fragment TrackingBreadcrumbsFragment on Breadcrumb {
    breadCrumb {
      name
      id
    }
  }
`);

export type TrackingProps = {
  breadCrumbData: FragmentType<typeof TrackingBreadcrumbsFragment> | undefined | null;
  productData: FragmentType<typeof TrackingFragment>;
  navq?: GtmProduct['category'];
};

/**
 * Tracking component used to track
 * */
export const Tracking: FC<TrackingProps> = ({ navq, productData, breadCrumbData }) => {
  const { position } = useHashParameters();
  const dispatchEvent = useTracking();
  const { setDefaultViewOutcome } = useAbTesting();
  const { asPath } = useRouter();
  const { hash, search } = getQueryStringsFromUrl(asPath);

  const {
    akl,
    availability,
    brand,
    dimensions,
    marketcode,
    name,
    orderNumber,
    price,
    promotion,
    sku,
    aplus,
    seoRatingIcons,
  } = getFragmentData(TrackingFragment, productData);

  useAddLastSeen(orderNumber);

  // TODO: outsource data formatting in helper -> https://empiriecom.atlassian.net/browse/NJS-784

  const breadcrumbs = getFragmentData(TrackingBreadcrumbsFragment, breadCrumbData);
  const categoryPathGA = breadcrumbs?.breadCrumb?.map((entry) => entry.name).join('/');
  const categoryPath: PageMetaDataProps['categoryPath'] = [];
  if (breadcrumbs?.breadCrumb && breadcrumbs?.breadCrumb?.length > 0) {
    breadcrumbs.breadCrumb.map((entry) => categoryPath.push({ id: entry.id, name: entry.name }));
  }

  let determinedAvailability: ProductCustomDimensions['dimension2'] | undefined;
  switch (availability.state) {
    case 'AVAILABLE':
      determinedAvailability = '1';
      break;
    case 'NOT_YET_AVAILABLE':
    case 'NOT_AVAILABLE':
      determinedAvailability = '0';
      break;
    case 'UNSELECTED':
    default:
      determinedAvailability = '2';
  }
  const oldPrice: GtmProduct['oldPrice'] = parseFloat(
    (price.valueOld ? price.valueOld / 100 : price.value / 100).toFixed(2),
  );
  const calculatedPrice: GtmProduct['price'] = parseFloat((price.value / 100).toFixed(2));

  // TODO: could we not just filter for displayName === "Größe" ?
  const size =
    dimensions
      ?.filter((dimension) => dimension?.type !== 'INPUT')
      .pop()
      ?.values.filter((value) => value.isSelected === true)
      .map((selectedValue) => selectedValue.displayText)
      .join('') ?? '';

  const variant = ([] as string[])
    .concat(
      ...dimensions.map((dimension) =>
        dimension.values
          .filter((dimObj) => dimObj.isSelected === true)
          .map((dimVal) => dimVal.displayText || ''),
      ),
    )
    .join('|');

  useEffect(() => {
    dispatchEvent<GtmEventProductView>({
      ecommerce: {
        detail: {
          products: [
            {
              availability: determinedAvailability,
              brand: brand?.name,
              category: navq ? `search:${navq}` : categoryPathGA,
              dimension1: availability.message,
              dimension2: determinedAvailability,
              dimension4: oldPrice,
              dimension5: sku,
              dimension6: akl,
              dimension8: promotion,
              id: orderNumber,
              marketcode: marketcode || undefined,
              name,
              oldPrice,
              position: position ? parseInt(position, 10) : 1,
              price: calculatedPrice,
              size,
              variant,
            },
          ],
        },
      },
      event: 'EECproductDetailView',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  /* PDVPageNavigation event: new product loaded */
  useEffect(() => {
    if (akl) {
      dispatchEvent<GtmEventPageNavigation>({
        event: 'ga_event',
        eventAction: 'NavigationClick',
        eventCategory: 'pageNavigation',
        eventLabel: 'DetailviewNavigation',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akl]);

  // product event for glycerin tracking
  useEffect(() => {
    // map availability state to glycerin enum names
    let glycerinAvailability: GlycerinAvailability;
    switch (availability.state) {
      case 'AVAILABLE':
        glycerinAvailability = GlycerinAvailability.IMMEDIATELY;
        break;
      case 'NOT_YET_AVAILABLE':
        glycerinAvailability = GlycerinAvailability.NOTIMMEDIATELY;
        break;
      case 'NOT_AVAILABLE':
        glycerinAvailability = GlycerinAvailability.UNAVAILABLE;
        break;
      case 'UNSELECTED':
      default:
        glycerinAvailability = GlycerinAvailability.UNKNOWN;
    }
    // map rating to glycerin enum names
    let glycerinRatingStatus: GlycerinRatingStatus = GlycerinRatingStatus.UNAVAILABLE;
    if (typeof seoRatingIcons?.product?.reviewCount !== 'undefined') {
      if (seoRatingIcons.product.reviewCount > 0) {
        glycerinRatingStatus = GlycerinRatingStatus.RATED;
      } else {
        glycerinRatingStatus = GlycerinRatingStatus.UNRATED;
      }
    }
    // create glycerin product payload
    const displayProductPayload: GlycerinDisplayProductPayload = {
      productId: akl,
      articleId: orderNumber,
      skuId: sku,
      displayedPrice: price.value,
      originalPrice: price.valueOld ?? undefined,
      currency: price.currency,
      availability: glycerinAvailability,
      ratingStatus: glycerinRatingStatus,
    };
    // add estimated delivery
    if (
      glycerinAvailability === GlycerinAvailability.IMMEDIATELY ||
      glycerinAvailability === GlycerinAvailability.NOTIMMEDIATELY
    ) {
      displayProductPayload.estimatedDelivery = availability.message;
    }
    // optional rating fields if product has been rated
    if (glycerinRatingStatus === GlycerinRatingStatus.RATED) {
      displayProductPayload.rating = seoRatingIcons?.product?.avgRating;
      displayProductPayload.ratingCount = seoRatingIcons?.product?.reviewCount;
    }
    // add services if available
    if (aplus?.clusters) {
      const services: GlycerinService[] = [];
      aplus.clusters.map(
        (cluster) =>
          cluster?.services.map((service) =>
            services.push({
              currency: price.currency,
              displayedPrice: service.gratis ? 0 : Math.round((service.price ?? 0) * 100),
              serviceType: cluster.label,
              name: service.name || '',
              quantity: service.preselected ? 1 : 0,
            }),
          ),
      );
      displayProductPayload.services = services;
    }
    // amount of available & unavailable variations from last product dimension
    // HINT: usually the last dimension of a product/variation is the "size" dimension which determines
    // the availability of the product
    const inStock = dimensions
      ?.filter((dimension) => dimension?.type !== 'INPUT')
      .pop()
      ?.values.filter((value) => value.availability?.state !== 'NOT_AVAILABLE').length;
    const outOfStock = dimensions
      ?.filter((dimension) => dimension?.type !== 'INPUT')
      .pop()
      ?.values.filter((value) => value.availability?.state === 'NOT_AVAILABLE').length;
    if (glycerinAvailability !== GlycerinAvailability.UNKNOWN) {
      if (inStock) {
        displayProductPayload.variantsInStock = inStock;
      }
      if (outOfStock) {
        displayProductPayload.variantsOutOfStock = outOfStock;
      }
    }
    // add origin if query params or hash contain ref param
    let origin: string | undefined;
    if (search?.indexOf('ref=') !== -1) {
      origin = search
        ?.split('&')
        .filter((value) => value.indexOf('ref=') !== -1 && value)
        .join('')
        .split('=')[1];
    } else if (hash?.indexOf('ref=') !== -1) {
      origin = hash
        ?.split('&')
        .filter((value) => value.indexOf('ref=') !== -1 && value)
        .join('')
        .split('=')[1];
    }
    if (origin && origin !== '') {
      displayProductPayload.origin = origin;
    }

    dispatchEvent<GTMEventGlycerinDisplayProduct>({
      DisplayProductData: displayProductPayload,
      event: 'DisplayProduct',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  // lackmus framework default view tracking for abtesting
  useEffect(() => {
    setDefaultViewOutcome(sku, akl, 'RPP');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akl, sku]);

  return (
    <PageMetaData
      page={{ type: 'detailview', template: 'detailview' }}
      categoryPath={categoryPath}
    />
  );
};
